/**
 * AP-773 — the reader page's single undo/redo history.
 *
 * One linear, in-memory, study-scoped stack of committed actions (spec §3.1).
 * A step is the user's action *plus every consequence of it* (spec §3.2):
 * its `undo`/`redo` closures restore the annotation, the worksheet value it
 * wrote, the derived values that followed, and the value source — together,
 * so the reader never sees a half-reverted state (invariant I2).
 *
 * Module-level external store (same shape as `studyLoadGesture.ts`) so the
 * viewer, the worksheet, and the page-level hotkeys all see one history.
 */
import { hasPendingShow, onShowIdle } from './viewerNavigation';

/** §3.1 action ids. A4 was withdrawn (adoption is A2 on an AI annotation). */
export type HistoryActionKind = 'A1' | 'A2' | 'A3' | 'A5' | 'A6' | 'A7' | 'A8';

/** Where an annotation step happened, so undo/redo can show it (R6). */
export interface HistoryLocation {
  viewportId: string;
  dicomUuid: string;
  /** 0-based frame index, as the viewer pane tracks it. */
  frame: number;
}

export interface HistoryStep {
  action: HistoryActionKind;
  /** Short human label for the R20 feedback message, e.g. "LVEDV value". */
  label: string;
  location?: HistoryLocation;
  /** Restore the exact pre-action state, consequences included.
   *  Return `false` when the step cannot be applied at all (R14): the store
   *  then leaves state as is, warns, and discards the step. */
  undo: () => void | boolean;
  /** Re-apply the action and its consequences. Same `false` contract. */
  redo: () => void | boolean;
}

/** R11: steps kept per study; the oldest is dropped beyond this. */
export const MAX_HISTORY_DEPTH = 5;

export interface HistoryState {
  undoStack: readonly HistoryStep[];
  redoStack: readonly HistoryStep[];
}

let state: HistoryState = { undoStack: [], redoStack: [] };
const listeners = new Set<() => void>();
let applying = false;
let recordingEnabled = true;

/** R8: while the report is not editable, nothing is recorded. Set by the
 *  reader page alongside the shortcuts (`useHistoryHotkeys`). */
export function setHistoryRecordingEnabled(enabled: boolean): void {
  recordingEnabled = enabled;
}

/** True while a step's undo/redo is executing. Viewer listeners use it to
 *  ignore the events a restore emits (a repaint tick is not a new drag). */
export function isApplyingHistory(): boolean {
  return applying;
}

/** Run `fn` with the applying flag raised. Steps that finish asynchronously
 *  (after a navigation, R6) wrap their deferred part in this too. */
export function runApplyingHistory(fn: () => void): void {
  const wasApplying = applying;
  applying = true;
  try {
    fn();
  } finally {
    applying = wasApplying;
  }
}

function emit() {
  listeners.forEach((l) => l());
}

export function subscribeHistory(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getHistoryState(): HistoryState {
  return state;
}

/** R1: one committed action → exactly one step on the undo stack.
 *  R2: a new action after undos discards the redo stack (linear history, I3). */
export function commitHistoryStep(step: HistoryStep): void {
  if (!recordingEnabled) return;
  const undoStack = [...state.undoStack, step].slice(-MAX_HISTORY_DEPTH);
  state = { undoStack, redoStack: [] };
  emit();
}

function warnDiscarded(kind: 'undo' | 'redo', step: HistoryStep): void {
  // eslint-disable-next-line no-console -- R14: the step's target no longer exists.
  console.warn(
    `[AP-773] ${kind}: step ${step.action} cannot be applied; discarded`,
    step.location
  );
}

/** What an undo / redo press did — the R20 feedback message reads this. */
export type HistoryResult =
  | { kind: 'empty' }
  | { kind: 'applied'; step: HistoryStep }
  | { kind: 'discarded'; step: HistoryStep }
  /** A step is still waiting for the viewer (R6); this press runs after it. */
  | { kind: 'queued' };

// Presses that arrived while a step was still waiting for the viewer. They
// run in order once it lands, so holding Cmd and tapping Z walks back step
// by step even across clips (I3) — never skipping or overwriting one.
const pressQueue: ('undo' | 'redo')[] = [];
let drainRegistered = false;

function drainPressQueue(): void {
  while (pressQueue.length > 0 && !hasPendingShow()) {
    const next = pressQueue.shift();

    if (next === 'undo') undoHistory();
    else if (next === 'redo') redoHistory();
  }
}

function queuePress(direction: 'undo' | 'redo'): HistoryResult {
  if (!drainRegistered) {
    onShowIdle(drainPressQueue);
    drainRegistered = true;
  }
  pressQueue.push(direction);
  return { kind: 'queued' };
}

/** R14, deferred form: a step whose target turned out not to exist once the
 *  viewer had navigated to it. Drops it from whichever stack holds it. */
export function discardHistoryStep(step: HistoryStep): void {
  if (!state.undoStack.includes(step) && !state.redoStack.includes(step)) {
    return;
  }
  // On the redo stack it was just undone; on the undo stack it is being
  // undone right now (immediate form) or was just redone.
  warnDiscarded(state.redoStack.includes(step) ? 'undo' : 'redo', step);
  state = {
    undoStack: state.undoStack.filter((s) => s !== step),
    redoStack: state.redoStack.filter((s) => s !== step),
  };
  emit();
}

/** Revert the most recent step and move it to the redo stack. */
export function undoHistory(): HistoryResult {
  // A previous step may still be waiting for the viewer to show its DICOM
  // (R6). This press runs after it lands, never before (I3).
  if (hasPendingShow()) return queuePress('undo');
  const step = state.undoStack[state.undoStack.length - 1];
  if (!step) return { kind: 'empty' };
  let applied = true;
  runApplyingHistory(() => {
    applied = step.undo() !== false;
  });
  if (!applied) {
    warnDiscarded('undo', step);
    state = { ...state, undoStack: state.undoStack.slice(0, -1) };
    emit();
    return { kind: 'discarded', step };
  }
  // The step may have discarded itself while running (R14, immediate form).
  if (state.undoStack[state.undoStack.length - 1] !== step) {
    return { kind: 'discarded', step };
  }
  state = {
    undoStack: state.undoStack.slice(0, -1),
    redoStack: [...state.redoStack, step],
  };
  emit();
  return { kind: 'applied', step };
}

/** Re-apply the most recently undone step and move it back to the undo stack. */
export function redoHistory(): HistoryResult {
  if (hasPendingShow()) return queuePress('redo');
  const step = state.redoStack[state.redoStack.length - 1];
  if (!step) return { kind: 'empty' };
  let applied = true;
  runApplyingHistory(() => {
    applied = step.redo() !== false;
  });
  if (!applied) {
    warnDiscarded('redo', step);
    state = { ...state, redoStack: state.redoStack.slice(0, -1) };
    emit();
    return { kind: 'discarded', step };
  }
  if (state.redoStack[state.redoStack.length - 1] !== step) {
    return { kind: 'discarded', step };
  }
  state = {
    undoStack: [...state.undoStack, step],
    redoStack: state.redoStack.slice(0, -1),
  };
  emit();
  return { kind: 'applied', step };
}

export function resetHistory(): void {
  pressQueue.length = 0;
  state = { undoStack: [], redoStack: [] };
  emit();
}
